import { NavigateNext, NavigateBefore } from '@mui/icons-material';
import {
  IconButton,
  LinearProgress,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button as MuiButton,
} from '@mui/material';
import { parse } from 'date-fns';
import { useEffect, useMemo, useRef, useState } from 'react';

import { scheduleToShifts, useOrderState } from '../../../context';
import type { Schedule } from '../../context';
import { useJobDraftActions, useJobDraftState } from '../../context';

import BreakCrumbHeaders from './Common/BreadCrumbHeaders';
import StaffingTabs from './Common/StaffingTabs';
import {
  NotFoundAlert,
  getHiredUpdatedSchedules,
  getMainHeading,
  getTabName,
  getTabValue,
} from './Common/StaffingUtils';
import StaffingWorkerProfileRow from './Common/StaffingWorkerProfileRow';

import useOvertimeGate from '@/hooks/useOvertimeGate';
import OTRiskBanner from '@/routes/Agency/shared/OTRiskBanner';
import { sumCurrentWeekShiftHours } from '@/routes/Agency/shared/currentWeekRange';

import Stack from '@/components/Stack';
import TableHeader from '@/components/TableHeader';
import TextInput from '@/components/TextInput';
import { Heading } from '@/components/Typography';
import { GAEvent } from '@/constants/gaevents';
import { WORKERS_LISTING_LIMIT } from '@/constants/general';
import { ApiEndpoints, V3Endpoints } from '@/constants/urls';
import Table from '@/elements/Table';
import useAuth from '@/hooks/useAuth';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import useMediaQuery from '@/hooks/useMediaQuery';
import { JobTypeEnum } from '@/types/graphql';
import axiosClient, { axiosRubyClient } from '@/util/axios/axiosClient';

import './index.css';
import useAnalytics from '@/util/analytics';
import { convertTZ } from '@/util/date';
import { workerName } from '@/util/worker';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';
import { useScheduleSelection } from './hooks/useScheduleSelection';
import { ShiftsSection } from './components/ShiftsSection';

type Props = {
  onClickJobAccess: () => void;
};

// Flatten a list of Schedule groups into the shifts payload accepted by
// /api/admin/overtime_projection_from_shifts. The caller is responsible
// for filtering past schedules. Exported (without the past-filter) so the
// derivation is unit-testable without mounting the wizard.
//
// GRAVY-10073 break-aware: each derived shift carries its parent
// schedule's `mandatoryBreakTime` as `mandatory_break_minutes`. Legacy
// breaks are per-schedule (every shift in the schedule shares the same
// break value) — the per-shift API contract just means each shift in the
// payload carries the inherited number.
export const deriveWizardProposedShifts = (
  nonPastSchedules: Schedule[],
  address: any,
): Array<{
  start_at: string;
  end_at: string;
  mandatory_break_minutes: number;
}> =>
  nonPastSchedules.flatMap((sch) =>
    scheduleToShifts(sch, address).map((s) => ({
      start_at: s.startAt,
      end_at: s.endAt,
      mandatory_break_minutes: sch.mandatoryBreakTime ?? 0,
    })),
  );

const MainInviteWorkersPage = ({ onClickJobAccess }: Props) => {
  const jobState = useJobDraftState();
  const { orderType, billing, jobs } = useOrderState();
  const { updateSchedules } = useJobDraftActions();
  const { schedules, skill, address } = jobState;
  const [tab, setTab] = useState(0);
  const { currentAdminIsCustomerAdmin: isClientAdmin } = useAuth();
  const [search, setSearch] = useState('');
  const debSearch = useDebouncedValue(search, 400);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isShowNavIcon, setIsShowNavIcon] = useState(false);
  const [disabledIcon, setDisabledIcon] = useState('left');
  const [workersList, setWorkersList] = useState<any[]>([]);

  const { logEvent } = useAnalytics();

  const container = document.getElementById('create-order-modal');
  const isLTA = orderType === JobTypeEnum.LTA;
  const skillName = jobState.skill?.name;
  const workerBadge = useFeatureValue(FEATURE_TOGGLE.WorkerBadge, false);
  const certificateIds =
    workerBadge && jobState.certificates.length > 0
      ? jobState.certificates.map((c) => c.id).join(',')
      : null;

  const jobFulfillment = useFeatureValue(FEATURE_TOGGLE.JobFulfillment, false);

  const {
    selectedSchedules,
    setSelectedSchedules,
    allSelected,
    handleSelectAllClick,
    handleShiftCardSelection,
  } = useScheduleSelection(schedules);

  const selectedSchedulesInfo = useMemo(() => {
    return schedules.filter((sch) => selectedSchedules.includes(sch.groupId));
  }, [schedules, selectedSchedules]);

  const containerRef = useRef<any | null>(null);

  const isPastScheduleJob = (sch) => {
    return (
      parse(sch.startTime, 'HH:mm', sch.dateRange.startDate!).getTime() <
      convertTZ(new Date(), address?.timezone).getTime()
    );
  };

  const allSchedulesArePast = () => {
    return schedules.every((sch) => {
      return isPastScheduleJob(sch);
    });
  };

  const allSelectedSchedulesArePast = () => {
    return selectedSchedulesInfo.every((sch) => {
      return isPastScheduleJob(sch);
    });
  };

  const TABLE_HEADERS = [
    { name: 'Worker Details', style: { color: '#262626' } },
    { name: skillName + ' Shifts', style: { width: '14%', color: '#262626' } },
    { name: 'Shifts With You', style: { width: '12%', color: '#262626' } },
    { name: 'Hours This Week', style: { width: '12%', color: '#262626' } },
    { name: 'Action', style: { width: '14%', color: '#262626' } },
  ];

  // GRAVY-10073: total hours of the schedules the admin currently has
  // selected (filtered to non-past), **net of per-shift mandatory break
  // minutes**. Drives the OT-risk banner above the worker list. Subtracts
  // the parent schedule's `mandatoryBreakTime` from each shift's gross
  // duration so the wizard's displayed hours agree with the backend
  // projection's net-of-break accounting. `Math.max` clamps negative
  // contributions from corrupt rows.
  const selectedShiftHours = useMemo(() => {
    const nonPast = selectedSchedulesInfo.filter(
      (sch) => !isPastScheduleJob(sch),
    );
    let total = 0;
    for (const shift of deriveWizardProposedShifts(nonPast, address)) {
      const startMs = new Date(shift.start_at).getTime();
      const endMs = new Date(shift.end_at).getTime();
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) continue;
      const breakHours = (shift.mandatory_break_minutes ?? 0) / 60;
      const netHours = (endMs - startMs) / 3_600_000 - breakHours;
      total += Math.max(netHours, 0);
    }
    return Number(total.toFixed(2));
  }, [selectedSchedulesInfo, address]);

  // Subset of the selected shifts that fall in the worker's current
  // Eastern payroll week (Mon 04:00). Feeds the per-row Hours This Week cell so
  // its at-risk red only fires when the admin's action would actually
  // push the worker over 40 *this week* (a future-week selection should
  // not flag this-week-already-at-40 workers as at-risk).
  const currentWeekSelectedShiftHours = useMemo(() => {
    const nonPast = selectedSchedulesInfo.filter(
      (sch) => !isPastScheduleJob(sch),
    );
    return sumCurrentWeekShiftHours(
      deriveWizardProposedShifts(nonPast, address),
    );
  }, [selectedSchedulesInfo, address]);

  /** **226 is the each shift section width and 20 is the gap between them(schedules)****/
  const schedulesContainerWidth =
    schedules.length * 226 + 20 * (schedules.length - 1);

  const fetchWorkers = async (page: number) => {
    setLoading(true);
    try {
      // Get all job details from jobState (current job being created)
      const jobParams = {
        // Basic job info
        jobSkillId: Number(jobState.skill?.id),
        jobAddressId: Number(jobState.address?.id),
        jobUniformId: jobState.uniform?.id,
        jobContactId: jobState.contact?.id,
        jobInstructions: jobState.instructions,
        jobAddressInstructions: jobState.addressInstructions,
        jobContactInstructions: jobState.contactInstructions,
        jobUniformInstructions: jobState.uniformInstructions,
        jobPostSetting: jobState.postSetting,
        jobPublishIn: jobState.publishIn,
        jobPublishJob: jobState.publishJob,
        jobType: jobState.jobType,
        jobRateId: jobState.rateId,

        // Schedules info
        jobSchedulesCount: jobState.schedules?.length || 0,
        jobSchedules: jobState.schedules
          ? JSON.stringify(
              jobState.schedules.map((schedule) => ({
                quantity: schedule.quantity,
                payRate: schedule.payRate,
                costRate: schedule.costRate,
                startTime: schedule.startTime,
                endTime: schedule.endTime,
                dateRange: schedule.dateRange,
                allShiftsSameWorker: schedule.allShiftsSameWorker,
                groupId: schedule.groupId,
                isHoliday: schedule.isHoliday,
                mandatoryBreakTime: schedule.mandatoryBreakTime,
                selectedWorkers: schedule.selectedWorkers?.map((w) => ({
                  id: w.id,
                  name: w.name,
                })),
                hiredWorkers: schedule.hiredWorkers?.map((w) => ({
                  id: w.id,
                  name: w.name,
                })),
              })),
            )
          : null,

        // Certificates
        jobCertificates: jobState.certificates
          ? JSON.stringify(
              jobState.certificates.map((cert) => ({
                id: cert.id,
                name: cert.name,
              })),
            )
          : null,

        // Rate information
        jobMarkupPercent: jobState.markupPercent,
        jobMinPay: jobState.minPay,
        jobMaxPay: jobState.maxPay,
        jobPayRate: jobState.payRate,

        // Skill category
        jobSkillCategory: jobState.skillCategory?.id,

        // Completion status
        jobCompletedStep: jobState.completedStep,
      };

      const res = jobFulfillment
        ? await axiosRubyClient.get(ApiEndpoints.GET_WORKERS_LIST, {
            params: {
              skillId: Number(skill?.id),
              customerId: Number(billing?.customer.id),
              addressId: Number(address?.id),
              page,
              perPage: WORKERS_LISTING_LIMIT,
              search: debSearch,
              tab: getTabValue(tab, !!skill?.gravyTrained),
              workerType: isLTA ? 'lta' : 'gig',
              taxType: billing?.customer.jobTaxType || 'all',
              badgeIds: certificateIds,
              backgroundCheckRequired: (billing?.customer as any)
                ?.requireBackgroundChecks,
              // Include all job details from jobState
              ...jobParams,
            },
          })
        : await axiosClient.post(V3Endpoints.GET_WORKERS_LIST, {
            skillId: Number(skill?.id),
            customerId: Number(billing?.customer.id),
            addressId: Number(address?.id),
            page,
            perPage: WORKERS_LISTING_LIMIT,
            search: debSearch,
            tab: getTabValue(tab, !!skill?.gravyTrained),
            workerType: isLTA ? 'lta' : 'gig',
            taxType: billing?.customer.jobTaxType || 'all',
            badgeIds: certificateIds,
            backgroundCheckRequired: (billing?.customer as any)
              ?.requireBackgroundChecks,
          });
      const respData = res?.data?.data || [];

      if (page > 1) {
        // For subsequent pages, filter out duplicates based on worker ID
        setWorkersList((prev) => {
          const existingIds = new Set(prev.map((worker) => worker.id));
          const newWorkers = respData.filter(
            (worker) => !existingIds.has(worker.id),
          );
          return [...prev, ...newWorkers];
        });
      } else {
        // For first page, set directly
        setWorkersList(respData);
      }

      setHasMore(respData.length > 0);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  let isThrottled = false;

  const handleScroll = () => {
    // Prevent multiple rapid calls
    if (isThrottled) return;

    if (container) {
      const scrollTop = container.scrollTop;
      const clientHeight = container.clientHeight;
      const scrollHeight = container.scrollHeight;

      // Add offset of 200px to trigger before hitting the bottom
      const offset = 200;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - offset;

      if (isNearBottom && hasMore && !loading) {
        isThrottled = true;
        setPage(page + 1);
        fetchWorkers(page + 1);

        // Reset throttle after a short delay
        setTimeout(() => {
          isThrottled = false;
        }, 300);
      }
    }
  };

  const [conflictWarning, setConflictWarning] = useState<{
    worker: Record<string, any>;
    mode?: string;
  } | null>(null);

  const { check: checkOvertime, modal: overtimeModal } = useOvertimeGate();

  const handleInviteWithConflictCheck = (
    worker: Record<string, any>,
    mode?: string,
  ) => {
    if (worker.hasConflicts && mode !== 'remove') {
      setConflictWarning({ worker, mode });
    } else {
      inviteButtonHandler(worker, mode);
    }
  };

  const performInvite = (worker: Record<string, any>, mode?: string) => {
    const updSchedules = schedules.map((sch) => {
      if (selectedSchedules.includes(sch.groupId) && !isPastScheduleJob(sch)) {
        if (mode === 'remove') {
          sch.selectedWorkers = sch.selectedWorkers.filter(
            (w) => w.id !== worker.id,
          );
        } else if (!sch.selectedWorkers.find((w) => w.id === worker.id)) {
          sch.selectedWorkers = [...sch.selectedWorkers, worker];
        }
      }
      return sch;
    });
    updateSchedules({ schedules: updSchedules });
    logEvent(GAEvent.JobInviteWorker, worker.id, {
      inviteSentFrom: 'Job Creation Page',
      tab: getTabValue(tab, !!skill?.gravyTrained),
    });
  };

  const inviteButtonHandler = (worker: Record<string, any>, mode?: string) => {
    // The wizard's jobs don't exist in the DB yet, so we can't ask
    // /api/admin/jobs/:id/overtime_projection. Flatten the selected
    // non-past schedules' shifts and let useOvertimeGate hit the
    // /api/admin/overtime_projection_from_shifts endpoint instead.
    //
    // We deliberately do NOT fall back to `worker.overtimeProjection`
    // (decorated by valid_workers) here even when it's non-null: that
    // projection is computed against a single backend jobId and the wizard
    // is staffing the worker across N selected schedules. The shifts
    // payload below is authoritative — it covers every selected schedule.
    const proposedShifts =
      mode === 'remove'
        ? []
        : deriveWizardProposedShifts(
            selectedSchedulesInfo.filter((sch) => !isPastScheduleJob(sch)),
            address,
          );

    checkOvertime(
      null,
      worker,
      mode === 'remove' ? 'remove' : 'invite',
      () => performInvite(worker, mode),
      { proposedShifts },
    );
  };

  const performHire = (worker: Record<string, any>, mode?: string) => {
    const { removeSelection, updatedSchedules } = getHiredUpdatedSchedules(
      schedules,
      selectedSchedules,
      worker,
      mode,
    );
    updateSchedules({ schedules: updatedSchedules });
    if (removeSelection.length > 0) {
      setSelectedSchedules(
        selectedSchedules.filter((id) => !removeSelection.includes(id)),
      );
    }
    logEvent(GAEvent.JobHireWorker, worker.id, {
      hiredFrom: 'Job Creation Page',
      tab: getTabValue(tab, !!skill?.gravyTrained),
    });
  };

  const hireButtonHandler = (worker: Record<string, any>, mode?: string) => {
    // Wizard jobs aren't in the DB yet, so gate via the shifts-based
    // projection exactly like inviteButtonHandler. Unhiring (mode 'remove')
    // short-circuits the gate so it never warns on removal.
    const proposedShifts =
      mode === 'remove'
        ? []
        : deriveWizardProposedShifts(
            selectedSchedulesInfo.filter((sch) => !isPastScheduleJob(sch)),
            address,
          );

    checkOvertime(
      null,
      worker,
      mode === 'remove' ? 'remove' : 'hire',
      () => performHire(worker, mode),
      { proposedShifts },
    );
  };

  const checkWorkerHiredOrInvited = (workerId: string, action = '') => {
    if (selectedSchedulesInfo.length === 0) return false;
    const checkProperty =
      action === 'hire' ? 'hiredWorkers' : 'selectedWorkers';

    for (let i = 0; i < selectedSchedulesInfo.length; i++) {
      const sch = selectedSchedulesInfo[i];
      if (!sch[checkProperty].find((w) => w.id === workerId)) {
        return false;
      }
    }
    return true;
  };

  const phoneOnly = useMediaQuery('(max-width: 559px)');

  const shiftsNavHandler = (rightNav = true) => {
    if (containerRef.current) {
      if (rightNav) {
        containerRef.current.scrollBy({ left: 500, behavior: 'smooth' });
      } else {
        containerRef.current.scrollBy({ left: -500, behavior: 'smooth' });
      }
      setTimeout(() => {
        if (containerRef.current.scrollLeft === 0) {
          setDisabledIcon('left');
        } else if (
          containerRef.current.scrollLeft +
            containerRef.current.clientWidth +
            1 >=
          containerRef.current.scrollWidth
        ) {
          setDisabledIcon('right');
        } else {
          setDisabledIcon('');
        }
      }, 550);
    }
  };

  useEffect(() => {
    if (debSearch) {
      logEvent(GAEvent.WorkerListSearch);
    }
    setPage(1);
    fetchWorkers(1);
  }, [tab, debSearch]);

  useEffect(() => {
    /** if shifts container width is greater then current window inner width minus(some page's padding)**/
    const isShowNextIcon = schedulesContainerWidth > window.innerWidth - 80;
    setIsShowNavIcon(isShowNextIcon);
  }, [schedules]);

  useEffect(() => {
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [hasMore, loading, debSearch, page]);

  return (
    <div style={{ position: 'relative', height: '100%' }}>
      {/* Loading Overlay */}
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <LinearProgress
            sx={{
              width: '200px',
              height: '4px',
            }}
          />
        </div>
      )}

      <BreakCrumbHeaders
        step={2}
        styles={{
          paddingLeft: phoneOnly ? 0 : '32px',
          marginBottom: '2px',
          top: '-5px',
        }}
        onClickJobAccess={() =>
          orderType !== JobTypeEnum.LTA && onClickJobAccess()
        }
      />
      <Stack
        css={{
          padding: phoneOnly ? '0px' : '0 32px',
          flexDirection: 'column',
        }}
        gap={10}
      >
        <Heading style={{ width: '100%', marginTop: '6px' }}>
          {getMainHeading(skillName, isClientAdmin, isLTA)}
        </Heading>

        {schedules.length > 0 && (
          <ShiftsSection
            containerRef={containerRef}
            isShowNavIcon={isShowNavIcon}
            phoneOnly={phoneOnly}
            disabledIcon={disabledIcon}
            allSelected={allSelected}
            selectedSchedules={selectedSchedules}
            selectedCount={selectedSchedules.length}
            schedules={schedules}
            isClientAdmin={isClientAdmin}
            isLTA={isLTA}
            onShiftCardSelection={handleShiftCardSelection}
            onSelectAll={handleSelectAllClick}
            onNavigate={shiftsNavHandler}
          />
        )}

        {!isLTA && (
          <StaffingTabs
            addressId={address?.id!}
            custId={billing?.customer.id!}
            isLTA={isLTA}
            jobTaxType={billing?.customer.jobTaxType!}
            setTab={(tab) => {
              setSearch('');
              setTab(tab);
            }}
            skill={skill!}
            tab={tab}
            badgeIds={certificateIds}
            backgroundCheckRequired={
              (billing?.customer as any)?.requireBackgroundChecks
            }
          />
        )}
        {/** ---workers table***/}
        <Stack
          vertical
          css={{ flexDirection: 'row', marginTop: '12px' }}
          gap={16}
        >
          {!phoneOnly && (
            <Heading style={{ width: '100%' }}>
              {isLTA ? 'LTA' : getTabName(tab, !!skill?.gravyTrained)}{' '}
              GravyWorkers
            </Heading>
          )}
          <div style={{ width: phoneOnly ? '100%' : '55%' }}>
            <TextInput
              id="search-GravyWorkers"
              placeholder="Search for GravyWorkers"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </Stack>
        <div style={{ width: '100%', marginTop: phoneOnly ? '18px' : '20px' }}>
          {loading && <LinearProgress />}
          {selectedShiftHours > 0 ? (
            <div style={{ margin: '0 0 12px' }}>
              <OTRiskBanner shiftHours={selectedShiftHours} />
            </div>
          ) : null}
          <Table
            css={phoneOnly ? { tableLayout: 'fixed', overflowX: 'clip' } : {}}
          >
            {!phoneOnly && (
              <TableHeader fields={TABLE_HEADERS} headerBgColor={'#EBEBEB'} />
            )}
            <tbody>
              {workersList.map((w) => (
                <StaffingWorkerProfileRow
                  key={w.id}
                  buttonsDisableCheck={selectedSchedules.length === 0}
                  hireButtonClick={hireButtonHandler}
                  inviteButtonClick={handleInviteWithConflictCheck}
                  inviteButtonDisable={
                    selectedSchedules.length === 0 ||
                    allSelectedSchedulesArePast()
                  }
                  isClientAdmin={isClientAdmin}
                  isHiredButton={checkWorkerHiredOrInvited(w.id, 'hire')}
                  isInvitedButton={checkWorkerHiredOrInvited(w.id)}
                  isLTA={isLTA || allSchedulesArePast()}
                  selectedShiftHours={currentWeekSelectedShiftHours}
                  skillName={skill?.name!}
                  worker={w}
                />
              ))}
            </tbody>
          </Table>
          {!loading && workersList.length === 0 && <NotFoundAlert tab={tab} />}
        </div>
      </Stack>

      {/* Conflict warning dialog when inviting unavailable worker */}
      <Dialog open={!!conflictWarning} onClose={() => setConflictWarning(null)}>
        <DialogTitle>Worker Has Schedule Conflicts</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {conflictWarning && (
              <>
                {`${workerName(conflictWarning.worker)} has existing shift${
                  (conflictWarning.worker.conflictDates?.length || 0) > 1
                    ? 's'
                    : ''
                } on ${conflictWarning.worker.conflictDates?.join(', ') || 'conflicting dates'}.`}
                {conflictWarning.worker.firstConflictJob && (
                  <>
                    <br />
                    {`Conflicting job: ${conflictWarning.worker.firstConflictJob.skillName || 'Job'} on ${conflictWarning.worker.firstConflictJob.date || ''}${conflictWarning.worker.firstConflictJob.startTime ? ` (${conflictWarning.worker.firstConflictJob.startTime} - ${conflictWarning.worker.firstConflictJob.endTime})` : ''}`}
                  </>
                )}
                <br />
                They will receive your invite but are currently booked for{' '}
                {(conflictWarning.worker.conflictDates?.length || 0) > 1
                  ? 'these dates'
                  : 'this date'}
                .
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setConflictWarning(null)}>Cancel</MuiButton>
          <MuiButton
            variant="contained"
            onClick={() => {
              if (conflictWarning) {
                inviteButtonHandler(
                  conflictWarning.worker,
                  conflictWarning.mode,
                );
              }
              setConflictWarning(null);
            }}
          >
            Send Invite
          </MuiButton>
        </DialogActions>
      </Dialog>

      {overtimeModal}
    </div>
  );
};

export default MainInviteWorkersPage;
